import React from 'react';

/**
 * CheckboxCard - Tappable card component for checkbox items
 *
 * Mobile-first design with:
 * - 48px+ tap targets
 * - Sage green selected state
 * - Full-width tap area
 * - Clear visual feedback
 */
export function CheckboxCard({
  id,
  label,
  description,
  isChecked,
  onToggle,
  hasFollowUp,
  followUpLabel,
  followUpPlaceholder,
  followUpValue,
  onFollowUpChange,
}) {
  const handleClick = () => {
    onToggle(id);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(id);
    }
  };

  return (
    <div>
      <div
        role="checkbox"
        aria-checked={isChecked}
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`
          relative flex items-start p-4 md:p-3 rounded-xl cursor-pointer
          transition-all duration-200 ease-out
          min-h-[60px] md:min-h-[52px]
          border-2
          ${isChecked
            ? 'bg-sage-50 border-sage-500 shadow-sage'
            : 'bg-background-card border-border-light hover:border-sage-300 hover:bg-sage-50/50'
          }
        `}
      >
        {/* Checkbox indicator */}
        <div
          className={`
            flex-shrink-0 w-6 h-6 rounded-md mr-4 mt-0.5
            flex items-center justify-center
            transition-all duration-200
            ${isChecked
              ? 'bg-sage-500'
              : 'bg-white border-2 border-neutral-300'
            }
          `}
        >
          {isChecked && (
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <span
            className={`
              block text-base font-medium leading-snug
              ${isChecked ? 'text-sage-800' : 'text-text-primary'}
            `}
          >
            {label}
          </span>
          {description && (
            <span
              className={`
                block text-sm mt-1 leading-relaxed
                ${isChecked ? 'text-sage-700' : 'text-text-muted'}
              `}
            >
              {description}
            </span>
          )}
        </div>
      </div>

      {/* Follow-up input (shown when checked and hasFollowUp is true) */}
      {isChecked && hasFollowUp && (
        <div className="mt-2 ml-10 animate-fade-in">
          <label className="block text-sm font-medium text-text-secondary mb-1">
            {followUpLabel}
          </label>
          <input
            type="text"
            value={followUpValue || ''}
            onChange={(e) => onFollowUpChange?.(id, e.target.value)}
            placeholder={followUpPlaceholder}
            className="
              w-full p-3 rounded-lg
              border border-border-light
              bg-white
              text-text-primary
              placeholder:text-text-muted
              focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400
              transition-all duration-200
            "
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default CheckboxCard;
